import { AUTHENTICATION_TYPES } from '@seek/apac-candidate-header-footer/types';
import {
  createContext,
  useContext,
  useEffect,
  useReducer,
  type ReactNode,
} from 'react';

import { getEnvironment } from 'src/core/config/globalStates';
import { getAuthentication } from 'src/modules/authentication/authentication';
import type { AppUserProfile } from 'src/types/userProfile';

interface AuthState {
  authStatus: AUTHENTICATION_TYPES;
  token: string | undefined;
  userProfile: AppUserProfile | undefined;
  environment: string;
  isLoading: boolean;
  error: Error | undefined;
  login: () => void;
}

type AuthAction =
  | { type: 'AUTH_START' }
  | {
      type: 'AUTH_SUCCESS';
      payload: {
        userProfile: AppUserProfile;
        token: string;
        authenticationStatus: AUTHENTICATION_TYPES;
      };
    }
  | { type: 'AUTH_FAILURE'; error: Error }
  | { type: 'LOGOUT' }
  | { type: 'SET_LOGIN'; login: () => void };

const initialState: AuthState = {
  authStatus: AUTHENTICATION_TYPES.AUTH_PENDING,
  token: undefined,
  userProfile: undefined,
  environment: getEnvironment(),
  isLoading: true,
  error: undefined,
  login: () => {},
};

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        isLoading: true,
        error: undefined,
      };

    case 'AUTH_SUCCESS':
      return {
        ...state,
        authStatus: AUTHENTICATION_TYPES.AUTHENTICATED,
        userProfile: action.payload.userProfile,
        token: action.payload.token,
        isLoading: false,
        error: undefined,
      };

    case 'AUTH_FAILURE':
      return {
        ...state,
        authStatus: AUTHENTICATION_TYPES.UNAUTHENTICATED,
        userProfile: undefined,
        token: undefined,
        isLoading: false,
        error: action.error,
      };

    case 'LOGOUT':
      return {
        ...state,
        authStatus: AUTHENTICATION_TYPES.UNAUTHENTICATED,
        userProfile: undefined,
        token: undefined,
        error: undefined,
      };

    case 'SET_LOGIN':
      return {
        ...state,
        login: action.login,
      };

    default:
      return state;
  }
};

interface AuthContextType {
  state: AuthState;
  logout: () => void;
  authenticate: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const authenticate = async () => {
    dispatch({ type: 'AUTH_START' });

    try {
      const authResult = await getAuthentication();

      if (!authResult) {
        throw new Error('Authentication failed - no result received');
      }

      dispatch({ type: 'SET_LOGIN', login: authResult.login });

      if (
        authResult.authenticationStatus ===
          AUTHENTICATION_TYPES.AUTHENTICATED &&
        authResult.userProfile &&
        authResult.token
      ) {
        dispatch({
          type: 'AUTH_SUCCESS',
          payload: {
            userProfile: authResult.userProfile,
            authenticationStatus: authResult.authenticationStatus,
            token: authResult.token,
          },
        });
      } else {
        dispatch({ type: 'LOGOUT' });
      }
    } catch (error) {
      dispatch({
        type: 'AUTH_FAILURE',
        error:
          error instanceof Error ? error : new Error('Authentication failed'),
      });
    }
  };

  const logout = () => {
    dispatch({ type: 'LOGOUT' });
  };

  // Only run authentication on client-side
  useEffect(() => {
    const isServer = typeof window === 'undefined';
    if (!isServer) {
      authenticate();
    }
  }, []);

  return (
    <AuthContext.Provider value={{ state, authenticate, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthStatus {
  authStatus: AUTHENTICATION_TYPES;
  userProfile: AppUserProfile | undefined;
  token: string | undefined;
  login: () => void;
  isAuthenticated: boolean;
}

export const useAuthStatus = (): AuthStatus => {
  const { state } = useAuth();
  return {
    authStatus: state.authStatus,
    userProfile: state.userProfile,
    token: state.token,
    login: state.login,
    isAuthenticated: state.authStatus === AUTHENTICATION_TYPES.AUTHENTICATED,
  };
};
